import type { GoogleAutocompletePlaceItem } from 'api/search'
import { getAutocompleteSuggestions } from 'api/search'
import { useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash.debounce'

interface AutocompleteParams {
  latitude?: number
  longitude?: number
  radius?: number
  filterPredictions?: (predictions: Array<GoogleAutocompletePlaceItem>) => Array<GoogleAutocompletePlaceItem>
  /**
   * Search term used to fetch initial predictions based on lat/lng/radius when the hook mounts.
   * These initial predictions are shown when textInput is empty.
   * Requires latitude and longitude to be provided.
   * When provided, enables the initial predictions feature.
   */
  initialSearchTerm?: string
}

function usePlaceAutocomplete(textInput: string, params: AutocompleteParams) {
  const [predictions, setPredictions] = useState<Array<GoogleAutocompletePlaceItem>>([])
  const [initialPredictionsData, setInitialPredictionsData] = useState<Array<GoogleAutocompletePlaceItem>>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(false)
  const initialPredictionsFetched = useRef(false)
  const requestIdRef = useRef(0)
  const initialRequestIdRef = useRef(0)

  const applyFilter = (
    predictions: Array<GoogleAutocompletePlaceItem>,
    filterFn?: (predictions: Array<GoogleAutocompletePlaceItem>) => Array<GoogleAutocompletePlaceItem>,
  ): Array<GoogleAutocompletePlaceItem> => {
    return filterFn ? filterFn(predictions) : predictions
  }

  const debouncedGetAutocompleteSuggestions = useMemo(() => debounce((textInput: string, params: AutocompleteParams, requestId: number) => {
    const hasLatLong = !!(params.latitude && params.longitude)
    getAutocompleteSuggestions(textInput, {
      origin: hasLatLong ? `${params.latitude},${params.longitude}` : undefined,
      location: hasLatLong ? `${params.latitude},${params.longitude}` : undefined,
      radius: params.radius,
    }).then((predictions) => {
      if (requestId !== requestIdRef.current) return
      setPredictions(applyFilter(predictions, params.filterPredictions))
    }).catch(() => {
      if (requestId !== requestIdRef.current) return
      setPredictions([])
    }).finally(() => {
      if (requestId !== requestIdRef.current) return
      setIsLoading(false)
    })
  }, 500), [])

  useEffect(() => {
    if (
      !params.initialSearchTerm ||
      !params.latitude ||
      !params.longitude ||
      initialPredictionsFetched.current
    ) {
      return
    }

    initialPredictionsFetched.current = true
    const initialRequestId = ++initialRequestIdRef.current
    setIsInitialLoading(true)

    getAutocompleteSuggestions(params.initialSearchTerm, {
      origin: `${params.latitude},${params.longitude}`,
      location: `${params.latitude},${params.longitude}`,
      radius: params.radius,
    }).then((predictions) => {
      if (initialRequestId !== initialRequestIdRef.current) return
      setInitialPredictionsData(applyFilter(predictions, params.filterPredictions))
    }).catch(() => {
      if (initialRequestId !== initialRequestIdRef.current) return
      setInitialPredictionsData([])
    }).finally(() => {
      if (initialRequestId !== initialRequestIdRef.current) return
      setIsInitialLoading(false)
    })
  }, [params.initialSearchTerm, params.latitude, params.longitude, params.radius, params.filterPredictions])

  useEffect(() => {
    if (!textInput) {
      requestIdRef.current += 1
      debouncedGetAutocompleteSuggestions.cancel()
      setPredictions([])
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    const requestId = ++requestIdRef.current
    debouncedGetAutocompleteSuggestions(textInput, params, requestId)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [textInput, params?.latitude, params?.longitude])

  useEffect(() => {
    return () => {
      requestIdRef.current += 1
      initialRequestIdRef.current += 1
      debouncedGetAutocompleteSuggestions.cancel()
    }
  }, [debouncedGetAutocompleteSuggestions])

  const activePredictions = textInput ? predictions : initialPredictionsData

  return {
    predictions: activePredictions,
    isLoading: textInput ? isLoading : isInitialLoading,
    initialPredictions: initialPredictionsData,
    searchPredictions: predictions,
  }
}

export default usePlaceAutocomplete
